import React from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Toaster } from 'sonner'

import InterviewPage from '@/pages/InterviewPage'
import WelcomePage from '@/pages/WelcomePage'
import CompletionPage from '@/pages/CompletionPage'
import { useAppSelector } from '@/hooks/redux'
import { ThemeProvider } from '@/contexts/ThemeContext'

// Admin components
import { AdminLogin } from '@/pages/admin/AdminLogin'
import { AdminLayout } from '@/pages/admin/AdminLayout'
import { Dashboard } from '@/pages/admin/Dashboard'
import { Organizations } from '@/pages/admin/Organizations'
import { JobTemplates } from '@/pages/admin/JobTemplates'
import { Interviews } from '@/pages/admin/Interviews'
import { Reports } from '@/pages/admin/Reports'
import { UserManagement } from '@/pages/admin/UserManagement'
import { AdminAuthGuard } from '@/components/admin/AdminAuthGuard'
import { InterviewAuthGuard } from '@/components/InterviewAuthGuard'
import { CompletionAuthGuard } from '@/components/CompletionAuthGuard'

const App: React.FC = () => {
  const { isAuthenticated } = useAppSelector((state) => state.auth)

  return (
    <ThemeProvider>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-snwolley-green-50">
        <Toaster 
          position="top-center"
          toastOptions={{
            style: {
              background: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '12px',
              fontSize: '1.05rem',
              fontWeight: 500,
            },
          }}
          theme="light"
          richColors
        />
      <AnimatePresence mode="wait">
        <Routes>
          <Route 
            path="/welcome" 
            element={
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <WelcomePage />
              </motion.div>
            } 
          />
          <Route 
            path="/interview" 
            element={
              <InterviewAuthGuard>
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 1.05 }}
                  transition={{ duration: 0.4 }}
                >
                  <InterviewPage />
                </motion.div>
              </InterviewAuthGuard>
            } 
          />
          <Route 
            path="/completed" 
            element={
              <CompletionAuthGuard>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.1 }}
                >
                  <CompletionPage />
                </motion.div>
              </CompletionAuthGuard>
            } 
          />
          <Route 
            path="/completion" 
            element={
              <CompletionAuthGuard>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.1 }}
                >
                  <CompletionPage />
                </motion.div>
              </CompletionAuthGuard>
            } 
          />
          {/* Admin routes */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route
            path="/admin"
            element={
              <AdminAuthGuard>
                <AdminLayout />
              </AdminAuthGuard>
            }
          >
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="organizations" element={<Organizations />} />
            <Route path="job-templates" element={<JobTemplates />} />
            <Route path="interviews" element={<Interviews />} />
            <Route path="reports" element={<Reports />} />
            <Route path="users" element={<UserManagement />} />
            <Route index element={<Navigate to="/admin/dashboard" replace />} />
          </Route>

          <Route path="/" element={<Navigate to="/welcome" replace />} />
          <Route path="*" element={<Navigate to="/welcome" replace />} />
        </Routes>
      </AnimatePresence>
      </div>
    </ThemeProvider>
  )
}

export default App